import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDay, whatsappLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, Plus, Building2, FileText, ShoppingCart, Check, X, XCircle, Pencil, Trash2, Download, Eye, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { downloadReport, downloadPurchaseOrder } from "@/lib/pdf";

export default function PurchasingPage() {
  const [tab, setTab] = useState("requests");
  const [suppliers, setSuppliers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pos, setPos] = useState([]);
  const [inv, setInv] = useState([]);

  const load = async () => {
    const [s, pr, po, i] = await Promise.all([
      api.get("/suppliers"), api.get("/purchase-requests"), api.get("/purchase-orders"), api.get("/inventory"),
    ]);
    setSuppliers(s.data); setRequests(pr.data); setPos(po.data); setInv(i.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="purchasing-page">
      <div className="mb-8">
        <div className="label-eyebrow">Procurement</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Purchasing</h1>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[#1a1a1a]">
        {[
          { k: "requests", label: "Requests", n: requests.length, icon: FileText },
          { k: "orders", label: "Purchase Orders", n: pos.length, icon: ShoppingCart },
          { k: "suppliers", label: "Suppliers", n: suppliers.length, icon: Building2 },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`pur-tab-${t.k}`}
              className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${tab === t.k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label} <span className="text-xs text-zinc-600">({t.n})</span>
            </button>
          );
        })}
      </div>

      {tab === "requests" && <RequestsTab requests={requests} inv={inv} suppliers={suppliers} reload={load} switchToOrders={() => setTab("orders")} />}
      {tab === "orders" && <OrdersTab pos={pos} suppliers={suppliers} requests={requests} inv={inv} reload={load} />}
      {tab === "suppliers" && <SuppliersTab suppliers={suppliers} reload={load} />}
    </div>
  );
}

function RequestsTab({ requests, inv, suppliers, reload, switchToOrders }) {
  const [show, setShow] = useState(false);
  const [editPr, setEditPr] = useState(null);
  const updateStatus = async (r, status) => {
    await api.patch(`/purchase-requests/${r.id}`, { status });
    toast.success(`Request ${status}`); reload();
  };
  const delPr = async (r) => {
    if (!window.confirm(`Delete request "${r.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/purchase-requests/${r.id}`);
      toast.success("Request deleted"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };
  const generatePOs = async (r) => {
    try {
      const { data } = await api.post(`/purchase-requests/${r.id}/generate-pos`);
      const n = data.created_pos?.length || 0;
      const u = data.unassigned?.length || 0;
      if (n > 0) toast.success(`Generated ${n} PO${n > 1 ? "s" : ""} (auto-grouped by supplier)`);
      if (u > 0) toast.warning(`${u} item${u > 1 ? "s" : ""} skipped — no primary supplier. Open Inventory to fix.`);
      reload();
      if (n > 0 && switchToOrders) setTimeout(switchToOrders, 600);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShow(true)} data-testid="new-pr" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]"><Plus className="w-4 h-4 mr-1.5" /> New Request</Button>
      </div>
      <div className="space-y-3">
        {requests.length === 0 && <div className="card-lux p-10 text-center text-zinc-600 text-sm">No purchase requests yet.</div>}
        {requests.map((r) => (
          <div key={r.id} className="card-lux p-5" data-testid={`pr-${r.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="label-eyebrow">{r.urgency}</div>
                <div className="font-display text-xl mt-1">{r.title}</div>
                <div className="text-xs text-zinc-500 mt-1">By {r.created_by_name} · {fmtDay(r.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditPr(r)} data-testid={`pr-edit-${r.id}`} title="Edit request"
                  className="p-1.5 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-[#D4AF37]"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => delPr(r)} data-testid={`pr-delete-${r.id}`} title="Delete request"
                  className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                <span className={`stage-chip capitalize ${
                  r.status === "approved" ? "text-emerald-400 border-emerald-500/30" :
                  r.status === "ordered" ? "text-blue-400 border-blue-500/30" :
                  r.status === "rejected" ? "text-red-400 border-red-500/30" :
                  r.status === "pending" ? "text-amber-400 border-amber-500/30" : "text-blue-400 border-blue-500/30"
                }`}>{r.status}</span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#1a1a1a] grid grid-cols-2 md:grid-cols-3 gap-2">
              {r.items.map((it, i) => {
                const invIt = (inv || []).find((x) => x.id === it.item_id);
                const supId = invIt?.primary_supplier_id || invIt?.supplier_id;
                const supName = (suppliers || []).find((s) => s.id === supId)?.name;
                return (
                  <div key={i} className="text-xs">
                    <span className="text-zinc-200">{it.name}</span>
                    <span className="text-zinc-500"> · {it.qty} {it.unit}</span>
                    {it.delivery_date && <div className="text-[10px] text-zinc-400">🗓 deliver {it.delivery_date}</div>}
                    {supName && <div className="text-[10px] text-[#D4AF37]/80">→ {supName}</div>}
                    {it.item_id && !supName && <div className="text-[10px] text-amber-400">⚠ No supplier</div>}
                  </div>
                );
              })}
            </div>
            {r.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <Button onClick={() => updateStatus(r, "approved")} data-testid={`pr-approve-${r.id}`} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                <Button onClick={() => updateStatus(r, "rejected")} data-testid={`pr-reject-${r.id}`} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-8"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
              </div>
            )}
            {r.status === "approved" && (
              <div className="mt-3 flex gap-2">
                <Button onClick={() => generatePOs(r)} data-testid={`pr-generate-pos-${r.id}`} className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] h-8">
                  <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Generate POs (auto-split by supplier)
                </Button>
              </div>
            )}
            {r.status === "ordered" && r.generated_po_ids?.length > 0 && (
              <div className="mt-2 text-xs text-zinc-500">
                {r.generated_po_ids.length} PO{r.generated_po_ids.length > 1 ? "s" : ""} created from this request.
              </div>
            )}
          </div>
        ))}
      </div>
      <PRForm open={show || !!editPr} editing={editPr} inv={inv} suppliers={suppliers} onClose={() => { setShow(false); setEditPr(null); }} onSaved={() => { setShow(false); setEditPr(null); reload(); toast.success(editPr ? "Request updated" : "Request submitted"); }} />
    </div>
  );
}

function OrdersTab({ pos, suppliers, requests, inv, reload }) {
  const [show, setShow] = useState(false);
  const [editPo, setEditPo] = useState(null);
  const sName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const updateStatus = async (p, status) => {
    try {
      await api.patch(`/purchase-orders/${p.id}`, { status });
      toast.success(`PO ${status}`); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const cancelPo = async (p) => {
    const reason = window.prompt(`Cancel ${p.po_number}? Enter a reason (kept for audit history):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required to cancel"); return; }
    try {
      await api.post(`/purchase-orders/${p.id}/cancel`, { reason: reason.trim() });
      toast.success("PO cancelled"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };
  const delPo = async (p) => {
    if (!window.confirm(`Delete ${p.po_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/purchase-orders/${p.id}`);
      toast.success("PO deleted"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const exportPDF = () => {
    downloadReport({
      title: "Purchase Orders Report",
      filename: `Purchase-Orders-${new Date().toISOString().slice(0, 10)}.pdf`,
      kpis: [
        { label: "Total POs", value: pos.length },
        { label: "Total Spend", value: fmtCurrency(pos.reduce((s, p) => s + (p.total || 0), 0)) },
        { label: "Pending Delivery", value: pos.filter((p) => p.status === "sent").length },
        { label: "Paid", value: pos.filter((p) => p.status === "paid").length },
      ],
      sections: [
        {
          title: "All Purchase Orders",
          columns: ["PO #", "Supplier", "Total", "Status", "Expected"],
          rows: pos.map((p) => [
            p.po_number, sName(p.supplier_id), fmtCurrency(p.total),
            p.status, p.expected_date ? fmtDay(p.expected_date) : "—",
          ]),
          columnStyles: { 2: { halign: "right" } },
        },
      ],
    });
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={exportPDF} data-testid="po-pdf" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
        <Button onClick={() => setShow(true)} data-testid="new-po" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]"><Plus className="w-4 h-4 mr-1.5" /> New PO</Button>
      </div>
      <div className="card-lux">
        <table className="w-full">
          <thead><tr className="text-left border-b border-[#1a1a1a] text-zinc-500"><th className="p-4 label-eyebrow text-[10px]">PO #</th><th className="p-4 label-eyebrow text-[10px]">Supplier</th><th className="p-4 label-eyebrow text-[10px]">Total</th><th className="p-4 label-eyebrow text-[10px]">Status</th><th className="p-4 label-eyebrow text-[10px] text-right">Actions</th></tr></thead>
          <tbody>
            {pos.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-zinc-600 text-sm">No purchase orders yet.</td></tr>}
            {pos.map((p) => {
              const supplier = suppliers.find((s) => s.id === p.supplier_id);
              return (
                <tr key={p.id} className="border-b border-[#1a1a1a] hover:bg-[#0c0c0c] transition-colors" data-testid={`po-${p.id}`}>
                  <td className="p-4 text-xs font-mono">
                    <Link to={`/purchasing/orders/${p.id}`} className="text-[#D4AF37] hover:text-[#E5C86B] underline-offset-2 hover:underline" data-testid={`po-link-${p.id}`}>{p.po_number}</Link>
                  </td>
                  <td className="p-4 text-sm">{sName(p.supplier_id)}</td>
                  <td className="p-4 text-sm">{fmtCurrency(p.total)}</td>
                  <td className="p-4"><span className="stage-chip capitalize text-[10px]">{p.status}</span>{p.approval_required && p.approval_status !== "approved" && <span className="ml-1.5 text-[9px] uppercase text-amber-400" data-testid={`po-needs-approval-${p.id}`}>needs approval</span>}</td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/purchasing/orders/${p.id}`} title="View details" data-testid={`po-view-${p.id}`}
                        className="p-1.5 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-[#D4AF37]"><Eye className="w-3.5 h-3.5" /></Link>
                      <button onClick={(e) => { e.stopPropagation(); downloadPurchaseOrder(p, supplier || {}); }} title="Download PDF" data-testid={`po-pdf-${p.id}`}
                        className="p-1.5 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-[#D4AF37]"><Download className="w-3.5 h-3.5" /></button>
                      {["draft", "sent"].includes(p.status) && (
                        <button onClick={() => setEditPo(p)} title="Edit PO" data-testid={`po-edit-${p.id}`}
                          className="p-1.5 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-[#D4AF37]"><Pencil className="w-3.5 h-3.5" /></button>
                      )}
                      {["draft", "sent", "partial"].includes(p.status) && (
                        <button onClick={() => cancelPo(p)} title="Cancel PO (keeps audit history)" data-testid={`po-cancel-${p.id}`}
                          className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"><XCircle className="w-3.5 h-3.5" /></button>
                      )}
                      {!["partial", "delivered", "paid"].includes(p.status) && (
                        <button onClick={() => delPo(p)} title="Delete PO" data-testid={`po-delete-${p.id}`}
                          className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                      {p.status === "draft" && <button onClick={() => updateStatus(p, "sent")} className="ml-1 text-xs text-[#D4AF37] hover:text-[#E5C86B]" data-testid={`po-send-${p.id}`}>Send →</button>}
                      {p.status === "sent" && <button onClick={() => updateStatus(p, "delivered")} className="ml-1 text-xs text-emerald-400" data-testid={`po-deliver-${p.id}`}>Delivered →</button>}
                      {p.status === "delivered" && <button onClick={() => updateStatus(p, "paid")} className="ml-1 text-xs text-blue-400" data-testid={`po-paid-${p.id}`}>Paid →</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <POForm open={show || !!editPo} editing={editPo} suppliers={suppliers} requests={requests} inv={inv} onClose={() => { setShow(false); setEditPo(null); }} onSaved={() => { setShow(false); setEditPo(null); reload(); toast.success(editPo ? "PO updated" : "PO created"); }} />
    </div>
  );
}

function SuppliersTab({ suppliers, reload }) {
  const [show, setShow] = useState(false);
  const [editSup, setEditSup] = useState(null);

  const del = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const exportPDF = () => {
    downloadReport({
      title: "Suppliers Directory",
      filename: `Suppliers-${new Date().toISOString().slice(0, 10)}.pdf`,
      kpis: [{ label: "Total Suppliers", value: suppliers.length }],
      sections: [
        {
          title: "All Suppliers",
          columns: ["Name", "Category", "Contact", "Phone", "Email", "Lead (days)", "Terms"],
          rows: suppliers.map((s) => [
            s.name, s.category, s.contact_person || "—", s.phone || "—",
            s.email || "—", String(s.lead_time_days || "—"), s.payment_terms || "—",
          ]),
        },
      ],
    });
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={exportPDF} data-testid="sup-pdf" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
        <Button onClick={() => setShow(true)} data-testid="new-supplier" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]"><Plus className="w-4 h-4 mr-1.5" /> New Supplier</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {suppliers.map((s) => (
          <div key={s.id} className="card-lux p-5 relative group" data-testid={`sup-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="label-eyebrow capitalize">{s.category}</div>
                <div className="font-display text-xl mt-1 truncate">{s.name}</div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditSup(s)} data-testid={`sup-edit-${s.id}`} title="Edit"
                  className="p-1.5 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-[#D4AF37]"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(s)} data-testid={`sup-del-${s.id}`} title="Delete"
                  className="p-1.5 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {s.contact_person && <div className="text-sm text-zinc-400 mt-1">{s.contact_person}</div>}
            {s.phone && <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1.5"><Phone className="w-3 h-3" /> {s.phone}</div>}
            <div className="text-xs text-zinc-600 mt-2">Lead time: {s.lead_time_days}d · {s.payment_terms}</div>
            {s.phone && (
              <a
                href={whatsappLink(s.phone, `Hi ${s.contact_person || s.name}, this is HNC Event Catering.`)}
                target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`sup-wa-${s.id}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/50 transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
          </div>
        ))}
      </div>
      <SupForm open={show} onClose={() => setShow(false)} onSaved={() => { setShow(false); reload(); toast.success("Supplier added"); }} />
      <SupForm open={!!editSup} editing={editSup} onClose={() => setEditSup(null)} onSaved={() => { setEditSup(null); reload(); toast.success("Supplier updated"); }} />
    </div>
  );
}

function PRForm({ open, editing, inv, suppliers = [], onClose, onSaved }) {
  const [f, setF] = useState({ title: "", urgency: "normal", needed_by: "", notes: "", items: [{ item_id: "", name: "", qty: 1, unit: "kg", delivery_date: "" }] });
  const [itemQ, setItemQ] = useState("");

  useEffect(() => {
    if (open) {
      setItemQ("");
      setF(editing
        ? { title: editing.title || "", urgency: editing.urgency || "normal", needed_by: editing.needed_by || "", notes: editing.notes || "", items: (editing.items || []).map((i) => ({ delivery_date: "", ...i })) }
        : { title: "", urgency: "normal", needed_by: "", notes: "", items: [{ item_id: "", name: "", qty: 1, unit: "kg", delivery_date: "" }] });
    }
  }, [open, editing]);

  const invFiltered = (() => {
    const q = itemQ.trim().toLowerCase();
    if (!q) return inv || [];
    return (inv || []).filter((x) => (x.name || "").toLowerCase().includes(q) || (x.sku || "").toLowerCase().includes(q) || (x.category || "").toLowerCase().includes(q));
  })();

  const pickInv = (idx, invId) => {
    const it = (inv || []).find((x) => x.id === invId);
    setF((prev) => {
      const items = [...prev.items];
      items[idx] = {
        ...items[idx],
        item_id: invId || "",
        name: it?.name || items[idx].name,
        unit: it?.unit || items[idx].unit,
      };
      return { ...prev, items };
    });
  };

  // Pre-fill from low stock alert
  const fillFromLowStock = () => {
    const low = (inv || []).filter((x) => x.qty <= x.reorder_level);
    if (low.length === 0) { toast.info("No items below par stock"); return; }
    setF((prev) => ({
      ...prev,
      title: prev.title || `Restock — ${low.length} items below par`,
      items: low.map((it) => ({
        item_id: it.id,
        name: it.name,
        qty: Math.max(it.reorder_level - it.qty, it.moq || 1),
        unit: it.unit,
      })),
    }));
    toast.success(`Loaded ${low.length} low-stock item${low.length > 1 ? "s" : ""}`);
  };

  const supName = (item_id) => {
    const it = (inv || []).find((x) => x.id === item_id);
    const sid = it?.primary_supplier_id || it?.supplier_id;
    return (suppliers || []).find((s) => s.id === sid)?.name;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit Purchase Request" : "New Purchase Request"}</DialogTitle>
          <DialogDescription className="sr-only">Submit a request for the purchaser/admin to review and convert into purchase orders.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Title (e.g. Weekly restock for kitchen)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="pr-title" className="bg-[#050505] border-[#2a2a2a]" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} data-testid="pr-urgency" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              <option value="normal">Normal</option><option value="urgent">Urgent</option><option value="emergency">Emergency</option>
            </select>
            <Input type="date" placeholder="Needed by" value={f.needed_by} onChange={(e) => setF({ ...f, needed_by: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="label-eyebrow text-[10px]">Items</label>
              <button type="button" onClick={fillFromLowStock} data-testid="pr-fill-low" className="text-[11px] text-[#D4AF37] hover:text-[#E5C86B]">⚡ Auto-fill low stock</button>
            </div>
            <Input value={itemQ} onChange={(e) => setItemQ(e.target.value)} placeholder="Search inventory items by name / SKU / category…" data-testid="pr-item-search" className="bg-[#0a0a0a] border-[#2a2a2a] h-9" />
            {itemQ && <div className="text-[10px] text-zinc-500">{invFiltered.length} item{invFiltered.length === 1 ? "" : "s"} match — dropdowns below show matches only</div>}
            {f.items.map((it, i) => (
              <div key={i} className="space-y-1.5 p-3 rounded-sm bg-[#050505] border border-[#1a1a1a]">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <select
                    value={it.item_id || ""}
                    onChange={(e) => pickInv(i, e.target.value)}
                    data-testid={`pr-pick-${i}`}
                    className="col-span-12 bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm px-2 h-8 text-xs"
                  >
                    <option value="">— Pick from inventory (recommended) —</option>
                    {it.item_id && !invFiltered.find((x) => x.id === it.item_id) && (() => { const cur = (inv || []).find((x) => x.id === it.item_id); return cur ? <option value={cur.id}>{cur.sku ? `[${cur.sku}] ` : ""}{cur.name} · {cur.qty} {cur.unit} in stock</option> : null; })()}
                    {invFiltered.map((x) => (
                      <option key={x.id} value={x.id}>{x.sku ? `[${x.sku}] ` : ""}{x.name} · {x.qty} {x.unit} in stock</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <Input placeholder="Item name" value={it.name} onChange={(e) => { const ni = [...f.items]; ni[i].name = e.target.value; setF({ ...f, items: ni }); }} className="col-span-4 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => { const ni = [...f.items]; ni[i].qty = Number(e.target.value); setF({ ...f, items: ni }); }} className="col-span-2 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <Input placeholder="Unit" value={it.unit} onChange={(e) => { const ni = [...f.items]; ni[i].unit = e.target.value; setF({ ...f, items: ni }); }} className="col-span-2 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <Input type="date" title="Delivery date for this item" value={it.delivery_date || ""} onChange={(e) => { const ni = [...f.items]; ni[i].delivery_date = e.target.value; setF({ ...f, items: ni }); }} data-testid={`pr-delivery-${i}`} className="col-span-3 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <button type="button" onClick={() => setF({ ...f, items: f.items.filter((_, idx) => idx !== i) })} className="col-span-1 text-zinc-500 hover:text-red-400" title="Remove row"><X className="w-4 h-4 mx-auto" /></button>
                </div>
                {it.item_id && (
                  <div className="text-[10px] text-zinc-500">
                    {supName(it.item_id) ? <>→ Will be routed to <span className="text-[#D4AF37]">{supName(it.item_id)}</span></> : <span className="text-amber-400">⚠ No primary supplier on this item — assign one in Inventory first.</span>}
                  </div>
                )}
              </div>
            ))}
            <Button variant="outline" type="button" onClick={() => setF({ ...f, items: [...f.items, { item_id: "", name: "", qty: 1, unit: "kg", delivery_date: "" }] })} className="border-[#2a2a2a]"><Plus className="w-3.5 h-3.5 mr-1" /> Add item</Button>
          </div>
          <Input placeholder="Notes / remarks for purchaser" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          <Button onClick={async () => {
            try {
              const body = { ...f, items: f.items.filter((i) => i.name) };
              if (editing) await api.patch(`/purchase-requests/${editing.id}`, body);
              else await api.post("/purchase-requests", body);
              onSaved?.();
            } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
          }} disabled={!f.title || !f.items.some((i) => i.name)} data-testid="pr-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">{editing ? "Save Changes" : "Submit Request"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function POForm({ open, editing, suppliers, requests, inv, onClose, onSaved }) {
  const [f, setF] = useState({ supplier_id: "", expected_date: "", request_id: "", items: [{ name: "", qty: 1, unit: "kg", unit_price: 0, item_id: "" }] });

  // Reset form on open so re-opens don't keep stale state
  useEffect(() => {
    if (open) setF(editing
      ? { supplier_id: editing.supplier_id || "", expected_date: editing.expected_date || "", request_id: editing.request_id || "", items: (editing.items || []).map((i) => ({ ...i })) }
      : { supplier_id: "", expected_date: "", request_id: "", items: [{ name: "", qty: 1, unit: "kg", unit_price: 0, item_id: "" }] });
  }, [open, editing]);

  const importFromRequest = (rid) => {
    const r = requests.find((x) => x.id === rid);
    if (r) setF((prev) => ({ ...prev, request_id: rid, items: r.items.map((i) => ({ ...i, unit_price: 0, item_id: "" })) }));
  };

  // Items visible in the "pick from inventory" dropdown:
  //  - if a supplier is selected → only items linked to that supplier
  //  - else → all inventory items
  const inventoryForPicker = f.supplier_id
    ? (inv || []).filter((it) => it.supplier_id === f.supplier_id)
    : (inv || []);

  const pickInventoryItem = (rowIdx, invId) => {
    const it = (inv || []).find((x) => x.id === invId);
    if (!it) return;
    setF((prev) => {
      const items = [...prev.items];
      items[rowIdx] = {
        ...items[rowIdx],
        item_id: it.id,
        name: it.name,
        unit: it.unit || items[rowIdx].unit,
        unit_price: it.price_per_unit || items[rowIdx].unit_price,
      };
      // Auto-select supplier if not yet chosen
      const nextSupplier = prev.supplier_id || it.supplier_id || "";
      return { ...prev, supplier_id: nextSupplier, items };
    });
  };

  const total = f.items.reduce((s, i) => s + (Number(i.qty || 0) * Number(i.unit_price || 0)), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-h-[85vh] overflow-y-auto max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? `Edit ${editing.po_number}` : "New Purchase Order"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow text-[10px]">Supplier</label>
            <select value={f.supplier_id} onChange={(e) => setF({ ...f, supplier_id: e.target.value })} data-testid="po-supplier" className="mt-1 w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              <option value="">Select supplier</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.category})</option>)}
            </select>
            {f.supplier_id && (
              <p className="text-[10px] text-zinc-500 mt-1">
                Inventory picker filtered to items linked to this supplier ({inventoryForPicker.length} item{inventoryForPicker.length === 1 ? "" : "s"}).
              </p>
            )}
          </div>
          <div>
            <label className="label-eyebrow text-[10px]">Linked Request (optional)</label>
            <select value={f.request_id} onChange={(e) => importFromRequest(e.target.value)} className="mt-1 w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              <option value="">No linked request</option>
              {requests.filter((r) => r.status === "approved").map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow text-[10px]">Expected Date</label>
            <Input type="date" value={f.expected_date} onChange={(e) => setF({ ...f, expected_date: e.target.value })} className="mt-1 bg-[#050505] border-[#2a2a2a]" />
          </div>
          <div className="space-y-2">
            <label className="label-eyebrow text-[10px]">Items</label>
            {f.items.map((it, i) => (
              <div key={i} className="space-y-1.5 p-3 rounded-sm bg-[#050505] border border-[#1a1a1a]">
                <select
                  value={it.item_id || ""}
                  onChange={(e) => pickInventoryItem(i, e.target.value)}
                  data-testid={`po-pick-inventory-${i}`}
                  className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm px-2 h-8 text-xs"
                >
                  <option value="">— Pick from inventory (optional) —</option>
                  {inventoryForPicker.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} · {x.qty} {x.unit} in stock · RM{x.price_per_unit}/{x.unit}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-12 gap-2">
                  <Input placeholder="Item name" value={it.name} onChange={(e) => { const ni = [...f.items]; ni[i].name = e.target.value; setF({ ...f, items: ni }); }} className="col-span-5 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => { const ni = [...f.items]; ni[i].qty = Number(e.target.value); setF({ ...f, items: ni }); }} className="col-span-2 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <Input placeholder="Unit" value={it.unit} onChange={(e) => { const ni = [...f.items]; ni[i].unit = e.target.value; setF({ ...f, items: ni }); }} className="col-span-2 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <Input type="number" step="0.01" placeholder="Price" value={it.unit_price} onChange={(e) => { const ni = [...f.items]; ni[i].unit_price = Number(e.target.value); setF({ ...f, items: ni }); }} className="col-span-2 bg-[#0a0a0a] border-[#2a2a2a] h-9" />
                  <button type="button" onClick={() => setF({ ...f, items: f.items.filter((_, idx) => idx !== i) })} className="col-span-1 text-zinc-500 hover:text-red-400" title="Remove row"><X className="w-4 h-4 mx-auto" /></button>
                </div>
              </div>
            ))}
            <Button variant="outline" type="button" onClick={() => setF({ ...f, items: [...f.items, { name: "", qty: 1, unit: "kg", unit_price: 0, item_id: "" }] })} className="border-[#2a2a2a]"><Plus className="w-3.5 h-3.5 mr-1" /> Add row</Button>
          </div>
          <div className="text-right text-sm">Total: <span className="text-[#D4AF37] font-display text-lg">{fmtCurrency(total)}</span></div>
          <Button disabled={!f.supplier_id} onClick={async () => {
            try {
              const items = f.items.filter((i) => i.name);
              if (editing) await api.patch(`/purchase-orders/${editing.id}`, { supplier_id: f.supplier_id, expected_date: f.expected_date, items });
              else await api.post("/purchase-orders", { ...f, items });
              onSaved?.();
            } catch (e) { toast.error(e.response?.data?.detail || "Failed to save PO"); }
          }} data-testid="po-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">{editing ? "Save Changes" : "Create PO"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SupForm({ open, editing, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", category: "vegetables", contact_person: "", phone: "", email: "", lead_time_days: 2, payment_terms: "NET 30" });
  const [cats, setCats] = useState([]);
  const [catEdit, setCatEdit] = useState(null); // null | {mode:'new'|'rename', id?, name}
  const loadCats = () => api.get("/supplier-categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { loadCats(); }, []);
  const saveCat = async () => {
    const name = (catEdit?.name || "").trim();
    if (!name) return;
    try {
      if (catEdit.mode === "rename") {
        await api.patch(`/supplier-categories/${catEdit.id}`, { name });
        toast.success("Category renamed — all its suppliers updated");
      } else {
        await api.post("/supplier-categories", { name });
        toast.success("Category added");
      }
      setF((prev) => ({ ...prev, category: name }));
      setCatEdit(null);
      loadCats();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteCat = async () => {
    const cur = cats.find((c) => c.name === f.category);
    if (!cur) return;
    if (!window.confirm(`Delete category "${cur.name}"? A category still used by suppliers can't be deleted.`)) return;
    try {
      await api.delete(`/supplier-categories/${cur.id}`);
      setF((prev) => ({ ...prev, category: "" }));
      loadCats();
      toast.success("Category deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  useEffect(() => {
    if (editing) {
      setF({
        name: editing.name || "", category: editing.category || "general",
        contact_person: editing.contact_person || "", phone: editing.phone || "",
        email: editing.email || "", lead_time_days: editing.lead_time_days || 2,
        payment_terms: editing.payment_terms || "NET 30",
      });
    } else if (open) {
      setF({ name: "", category: "vegetables", contact_person: "", phone: "", email: "", lead_time_days: 2, payment_terms: "NET 30" });
    }
  }, [editing, open]);

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/suppliers/${editing.id}`, f);
      } else {
        await api.post("/suppliers", f);
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <Dialog open={!!(open || editing)} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sup-name" className="bg-[#050505] border-[#2a2a2a]" />
          <div>
            <div className="flex items-stretch gap-1">
              <select value={f.category || ""} data-testid="sup-category"
                onChange={(e) => { if (e.target.value === "__new__") { setCatEdit({ mode: "new", name: "" }); return; } setF({ ...f, category: e.target.value }); }}
                className="flex-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm capitalize">
                <option value="">— Category —</option>
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                {f.category && !cats.find((c) => c.name === f.category) && <option value={f.category}>{f.category}</option>}
                <option value="__new__">＋ New category…</option>
              </select>
              {f.category && cats.find((c) => c.name === f.category) && (
                <>
                  <Button type="button" onClick={() => setCatEdit({ mode: "rename", id: cats.find((c) => c.name === f.category).id, name: f.category })} variant="outline" data-testid="sup-category-rename" title="Rename this category"
                    className="h-10 w-10 p-0 border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button type="button" onClick={deleteCat} variant="outline" data-testid="sup-category-delete" title="Delete this category"
                    className="h-10 w-10 p-0 border-[#2a2a2a] hover:border-red-500 hover:text-red-400 rounded-sm"><Trash2 className="w-3.5 h-3.5" /></Button>
                </>
              )}
            </div>
            {catEdit && (
              <div className="mt-1.5">
                <div className="flex items-center gap-1.5">
                  <Input autoFocus value={catEdit.name} onChange={(e) => setCatEdit({ ...catEdit, name: e.target.value })}
                    placeholder={catEdit.mode === "rename" ? "New name" : "e.g. spices"} data-testid="sup-category-name-input"
                    className="h-8 bg-[#050505] border-[#2a2a2a] text-sm" />
                  <Button type="button" onClick={saveCat} data-testid="sup-category-save" className="h-8 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm text-xs px-3">{catEdit.mode === "rename" ? "Rename" : "Add"}</Button>
                  <Button type="button" onClick={() => setCatEdit(null)} variant="outline" className="h-8 border-[#2a2a2a] rounded-sm text-xs px-3">Cancel</Button>
                </div>
                {catEdit.mode === "rename" && <p className="text-[10px] text-zinc-500 mt-1">Renaming updates every supplier using this category.</p>}
              </div>
            )}
          </div>
          <Input placeholder="Contact person" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          <Input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          <Input placeholder="Email (optional)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Lead days" value={f.lead_time_days} onChange={(e) => setF({ ...f, lead_time_days: Number(e.target.value) })} className="bg-[#050505] border-[#2a2a2a]" />
            <Input placeholder="Payment terms" value={f.payment_terms} onChange={(e) => setF({ ...f, payment_terms: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          </div>
          <Button onClick={save} data-testid="sup-save" disabled={!f.name} className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">{editing ? "Save Changes" : "Add Supplier"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
